import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '../../../utils/auth';

const STORAGE_KEY = 'veiassistent_notification_prefs';

const defaultPrefs = {
  emailNotifications: true,
  smsNotifications: false,
  pushNotifications: true,
};

const AccountSettings = () => {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [toast, setToast] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0); // 0=closed, 1=confirm, 2=final
  const [deleteInput, setDeleteInput] = useState('');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setPrefs(JSON.parse(saved));
    } catch {}
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleToggle = (key) => {
    const updated = { ...prefs, [key]: !prefs?.[key] };
    setPrefs(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      showToast('Innstillinger lagret', 'success');
    } catch {
      showToast('Kunne ikke lagre innstillinger', 'error');
    }
  };

  const handleExportData = () => {
    try {
      const session = getSession();
      const notifPrefs = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      const twoFAData = JSON.parse(localStorage.getItem('veiassistent_2fa') || '{}');
      const exportData = {
        exportedAt: new Date()?.toISOString(),
        account: {
          name: session?.name || session?.fullName || '',
          email: session?.email || '',
          phone: session?.phone || '',
          accountTier: session?.accountTier || 'free',
          language: session?.language || 'no',
          address: session?.address || {},
          createdAt: session?.createdAt || null,
        },
        notificationPreferences: notifPrefs,
        security: {
          twoFactorEnabled: twoFAData?.enabled || false,
          twoFactorPhone: twoFAData?.phone || null,
        },
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `veiassistent-data-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Data eksportert', 'success');
    } catch {
      showToast('Eksport mislyktes', 'error');
    }
  };

  const handleDeleteAccount = () => {
    // Clear all user data from localStorage
    const keysToRemove = [
      'veiassistent_session',
      'veiassistent_notification_prefs',
      'veiassistent_2fa',
      'veiassistent_notifications',
    ];
    keysToRemove?.forEach(k => localStorage.removeItem(k));
    setDeleteStep(0);
    navigate('/');
  };

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '24px',
  };

  const accentBarStyle = {
    width: '3px',
    height: '18px',
    background: 'var(--color-accent)',
    borderRadius: '2px',
    flexShrink: 0,
  };

  const sectionLabelStyle = {
    fontFamily: 'var(--font-heading)',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--color-muted-foreground)',
  };

  const toggleNotifs = [
    {
      key: 'emailNotifications',
      label: 'E-postvarsler',
      description: 'Motta varsler om fakturaer, abonnementsendringer og viktige oppdateringer via e-post',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      key: 'smsNotifications',
      label: 'SMS-varsler',
      description: 'Motta tekstmeldinger for betalingsbekreftelser og kritiske kontovarsler',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="4" y="2" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="10" cy="15" r="1" fill="currentColor"/>
          <line x1="7" y1="6" x2="13" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="7" y1="9" x2="11" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      key: 'pushNotifications',
      label: 'Push-varsler',
      description: 'Motta sanntidsvarsler i nettleseren for trafikkoppdateringer og bompasseringer',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 2C7.24 2 5 4.24 5 7v5l-1.5 2h13L15 12V7c0-2.76-2.24-5-5-5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M8 16.5c0 1.1.9 2 2 2s2-.9 2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
    },
  ];

  return (
    <div style={{ position: 'relative' }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '32px',
            right: '32px',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 22px',
            background: toast?.type === 'success' ? 'var(--color-foreground)' : '#DC2626',
            borderRadius: 'var(--radius-sm)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            animation: 'slideInUp 0.3s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <style>{`
            @keyframes slideInUp {
              from { opacity: 0; transform: translateY(16px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          {toast?.type === 'success' && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" stroke="#4ADE80" strokeWidth="1.5"/>
              <path d="M5.5 9L7.8 11.5L12.5 6.5" stroke="#4ADE80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', letterSpacing: '0.04em' }}>
            {toast?.message}
          </span>
        </div>
      )}
      {/* ── Notification Preferences ── */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={sectionHeaderStyle}>
          <div style={accentBarStyle} />
          <span style={sectionLabelStyle}>Varslingsinnstillinger</span>
        </div>
        <p style={{ fontFamily: 'var(--font-caption)', fontSize: '14px', color: 'var(--color-muted-foreground)', marginBottom: '28px', lineHeight: 1.6 }}>
          Velg hvilke varsler du ønsker å motta. Innstillingene lagres automatisk.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {toggleNotifs?.map((item, idx) => (
            <div
              key={item?.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '20px 0',
                borderBottom: idx < toggleNotifs?.length - 1 ? '1px solid var(--color-border)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1 }}>
                <div style={{ color: prefs?.[item?.key] ? 'var(--color-accent)' : 'var(--color-muted-foreground)', marginTop: '2px', flexShrink: 0, transition: 'color 200ms ease' }}>
                  {item?.icon}
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '4px' }}>
                    {item?.label}
                  </div>
                  <div style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>
                    {item?.description}
                  </div>
                </div>
              </div>
              {/* Toggle switch */}
              <button
                onClick={() => handleToggle(item?.key)}
                aria-label={`Toggle ${item?.label}`}
                style={{
                  position: 'relative',
                  width: '48px',
                  height: '26px',
                  background: prefs?.[item?.key] ? 'var(--color-accent)' : 'rgba(0,0,0,0.15)',
                  border: 'none',
                  borderRadius: '13px',
                  cursor: 'pointer',
                  transition: 'background 200ms ease',
                  flexShrink: 0,
                  outline: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '3px',
                    left: prefs?.[item?.key] ? '25px' : '3px',
                    width: '20px',
                    height: '20px',
                    background: '#FFFFFF',
                    borderRadius: '50%',
                    transition: 'left 200ms cubic-bezier(0.22,1,0.36,1)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
      {/* ── Data Export ── */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={sectionHeaderStyle}>
          <div style={accentBarStyle} />
          <span style={sectionLabelStyle}>Dataeksport</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--color-foreground)', fontWeight: 500, marginBottom: '6px' }}>
              Last ned alle dine data
            </p>
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)', lineHeight: 1.6 }}>
              Eksporter kontodata, varslingsinnstillinger og sikkerhetskonfigurasjon som en JSON-fil.
            </p>
          </div>
          <button
            onClick={handleExportData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 22px',
              background: 'transparent',
              border: '2px solid var(--color-accent)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-heading)',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-accent)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 200ms ease, transform 200ms cubic-bezier(0.22,1,0.36,1)',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,87,34,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
            onMouseDown={e => e.currentTarget.style.transform = 'scale(0.96)'}
            onMouseUp={e => e.currentTarget.style.transform = 'translateY(-1px)'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Eksporter JSON
          </button>
        </div>
      </div>
      {/* ── Danger Zone: Account Deletion ── */}
      <div
        className="card"
        style={{
          border: '2px solid rgba(185,28,28,0.25)',
          background: 'rgba(185,28,28,0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
          <div style={{ width: '3px', height: '18px', background: '#B91C1C', borderRadius: '2px', flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#B91C1C' }}>
            Faresone
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--color-foreground)', fontWeight: 500, marginBottom: '6px' }}>
              Slett konto
            </p>
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)', lineHeight: 1.6 }}>
              Permanent sletting av kontoen din og alle tilknyttede data. Denne handlingen kan ikke angres.
            </p>
          </div>
          <button
            onClick={() => setDeleteStep(1)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 22px',
              background: 'transparent',
              border: '2px solid #B91C1C',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-heading)',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#B91C1C',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 200ms ease, transform 200ms cubic-bezier(0.22,1,0.36,1)',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(185,28,28,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
            onMouseDown={e => e.currentTarget.style.transform = 'scale(0.96)'}
            onMouseUp={e => e.currentTarget.style.transform = 'translateY(-1px)'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 9a1 1 0 001 1h6a1 1 0 001-1l1-9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Slett konto
          </button>
        </div>
      </div>
      {/* ── Step 1: Consequences Dialog ── */}
      {deleteStep === 1 && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={e => { if (e?.target === e?.currentTarget) setDeleteStep(0); }}
        >
          <div
            style={{
              background: 'var(--color-card)',
              border: '2px solid rgba(185,28,28,0.3)',
              borderRadius: 'var(--radius-md)',
              padding: '40px',
              maxWidth: '520px',
              width: '100%',
              boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
              animation: 'slideInUp 0.3s cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            {/* Warning icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
              <div style={{ width: '44px', height: '44px', background: 'rgba(185,28,28,0.1)', border: '2px solid rgba(185,28,28,0.3)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M11 3L2 19h18L11 3z" stroke="#B91C1C" strokeWidth="1.8" strokeLinejoin="round"/>
                  <line x1="11" y1="9" x2="11" y2="13" stroke="#B91C1C" strokeWidth="1.8" strokeLinecap="round"/>
                  <circle cx="11" cy="16" r="1" fill="#B91C1C"/>
                </svg>
              </div>
              <div>
                <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: '#B91C1C', marginBottom: '2px' }}>
                  Slett konto permanent
                </h3>
                <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)' }}>
                  Les konsekvensene nøye
                </p>
              </div>
            </div>

            {/* Consequences list */}
            <div style={{ background: 'rgba(185,28,28,0.06)', border: '1px solid rgba(185,28,28,0.2)', borderRadius: 'var(--radius-sm)', padding: '20px', marginBottom: '28px' }}>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#B91C1C', marginBottom: '14px' }}>
                Dette vil skje:
              </p>
              {[
                'All kontodata og profilinformasjon slettes permanent',
                'Abonnementet ditt avsluttes umiddelbart uten refusjon',
                'Kjøretøyregistreringer og historikk fjernes',
                'Fakturaer og betalingshistorikk slettes',
                'Du mister tilgang til alle Veiassistent-tjenester',
                'Handlingen kan ikke angres',
              ]?.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: i < 5 ? '10px' : 0 }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginTop: '2px', flexShrink: 0 }}>
                    <circle cx="7" cy="7" r="6" stroke="#B91C1C" strokeWidth="1.2"/>
                    <path d="M4.5 4.5l5 5M9.5 4.5l-5 5" stroke="#B91C1C" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  <span style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-foreground)', lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setDeleteStep(0)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'transparent',
                  border: '2px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--color-muted-foreground)',
                  cursor: 'pointer',
                  transition: 'border-color 200ms ease, color 200ms ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-foreground)'; e.currentTarget.style.color = 'var(--color-foreground)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
              >
                Avbryt
              </button>
              <button
                onClick={() => { setDeleteStep(2); setDeleteInput(''); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#B91C1C',
                  border: '2px solid #B91C1C',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'opacity 200ms ease',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Fortsett
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Step 2: Type to confirm ── */}
      {deleteStep === 2 && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={e => { if (e?.target === e?.currentTarget) setDeleteStep(0); }}
        >
          <div
            style={{
              background: 'var(--color-card)',
              border: '2px solid rgba(185,28,28,0.3)',
              borderRadius: 'var(--radius-md)',
              padding: '40px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
              animation: 'slideInUp 0.3s cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: '#B91C1C', marginBottom: '8px' }}>
              Bekreft sletting
            </h3>
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '14px', color: 'var(--color-muted-foreground)', lineHeight: 1.6, marginBottom: '28px' }}>
              Skriv <strong style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>SLETT KONTO</strong> i feltet nedenfor for å bekrefte permanent sletting.
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={e => setDeleteInput(e?.target?.value)}
              placeholder="SLETT KONTO"
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${deleteInput === 'SLETT KONTO' ? '#B91C1C' : 'var(--color-border)'}`,
                borderRadius: 0,
                padding: '10px 0',
                fontFamily: 'var(--font-heading)',
                fontSize: '15px',
                fontWeight: 600,
                letterSpacing: '0.06em',
                color: 'var(--color-foreground)',
                outline: 'none',
                marginBottom: '28px',
                transition: 'border-color 200ms ease',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setDeleteStep(0)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'transparent',
                  border: '2px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--color-muted-foreground)',
                  cursor: 'pointer',
                  transition: 'border-color 200ms ease, color 200ms ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-foreground)'; e.currentTarget.style.color = 'var(--color-foreground)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteInput !== 'SLETT KONTO'}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: deleteInput === 'SLETT KONTO' ? '#B91C1C' : 'rgba(185,28,28,0.2)',
                  border: `2px solid ${deleteInput === 'SLETT KONTO' ? '#B91C1C' : 'rgba(185,28,28,0.2)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: deleteInput === 'SLETT KONTO' ? '#FFFFFF' : 'rgba(185,28,28,0.4)',
                  cursor: deleteInput === 'SLETT KONTO' ? 'pointer' : 'not-allowed',
                  transition: 'all 200ms ease',
                }}
              >
                Slett permanent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
